import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ChipColorStyles } from "_interfaces/_common";
import { FilterPlansProps } from "_interfaces/_common/filterPlans";
import {
  CheckoutBillInterface,
  FETCH_PAYMENT_TIMEOUT_MINUTES,
  PaymentMethodDataProps,
} from "_interfaces/_common/payment";
import {
  CouponApplyOnEnum,
  CouponCheckApplicabilityProps,
} from "_interfaces/http-requests/coupon";
import {
  BuildBundleRechargeRequestProps,
  BundleRechargeRequestProps,
} from "_interfaces/http-requests/recharge";
import { ApplyCouponModel } from "_models/data/payment/data.applyCoupon.model";
import { WalletPaymentModel } from "_models/data/wallet/data.walletPayment.model";
import { ApplyCouponErrorModel } from "_models/errors/payment/error.applyCoupon.model";
import axios from "axios";
import PaymentCheckout from "component/_common/payment/paymentCheckout";
import { paymentOptionsList } from "component/_common/payment/paymentOptionsList";
import WalletPaymentMethod from "component/_common/payment/walletPaymentMethod";
import BuildBundleCard from "component/_common/plans/buildBundleCard";
import PlanCardFour from "component/_common/plans/planCardFour";
import SummaryNumber from "component/_common/plans/summaryNumber";
import useProcessStatusModal from "component/_common/status/useProcessStatusModal";
import { AppStatusCode } from "config/appStatusCode";
import {
  filterNonNullValues,
  formatDataUsage,
  formatPriceWithDelimiter,
} from "functions/helper";
import { HTTP_ERROR } from "functions/http";
import { CouponCheckApplicability } from "functions/http-requests/coupon";
import {
  RechargeBuildBundle,
  RechargeBundle,
  RechargeFulfill,
} from "functions/http-requests/recharge";
import RouteConstant from "navigation/constant";
import MainContainer from "parts/mainContainer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAlert } from "state/slice/alert";
import { toggleLoading } from "state/slice/loading";
import { DispatchRechargeData } from "state/slice/rechargePlan";
import { RootState } from "state/store";
import AllPlans from "./allPlans";
import BuildBundleRecharge from "./buildBundleRecharge";
import FilterPlans from "./filterPlans";

const RechargePlansLayout = () => {
  const Dispatch = useDispatch();
  const navigate = useNavigate();
  const { breakpoints, palette } = useTheme();
  const isMediumDevice = useMediaQuery(breakpoints.up("md"));

  const { userInfo } = useSelector((state: RootState) => state.user);
  const { rechargeData } = useSelector(
    (state: RootState) => state.rechargePlan,
  );
  const { bookingData } = useSelector((state: RootState) => state.phoneBooking);

  const [filters, setFilters] = useState<FilterPlansProps>({
    packageFor: "",
    category: "",
    packageType: undefined,
  });

  const [buildBundleRechargeOpen, setBuildBundleRechargeOpen] =
    useState<boolean>(false);

  const [openPaymentOption, setOpenPaymentOption] = useState<boolean>(false);
  const [openWalletPaymentMethod, setOpenWalletPaymentMethod] =
    useState<boolean>(false);

  const [activeRechargeId, setActiveRechargeId] = useState<string>("");
  const [processingSnapshot, setProcessingSnapshot] = useState<{
    phoneNumber?: string;
    amount?: number;
    paymentBy?: string;
    paymentMethod?: string;
    packageName?: string;
    packageType?: string;
    packageBenefit?: string;
    couponCode?: string;
  }>({});
  const [processFailedMessage, setProcessFailedMessage] = useState<string>("");
  const DEFAULT_RECHARGE_FAILED_MESSAGE =
    "We could not confirm your recharge right now. Please wait a few minutes and try again. If you were charged, it may still be completed automatically.";

  const RECHARGE_POLL_SECONDS = 10;
  const RECHARGE_POLL_TIMEOUT_SECONDS = FETCH_PAYMENT_TIMEOUT_MINUTES * 60;
  const formatCount = (value?: string | number) => {
    const count = Number(value || 0);
    return count > 0 ? formatPriceWithDelimiter(count) : "0";
  };

  const rechargeProcessModal = useProcessStatusModal({
    timeoutSeconds: RECHARGE_POLL_TIMEOUT_SECONDS,
    processingGuardMessage:
      "Recharge is being processed. Do not refresh, close, or go back.",
  });
  const {
    open: processModalOpen,
    status: processModalStatus,
    countdownLabel: processCountdownLabel,
    startProcessing: startProcessModal,
    setFailed: setProcessFailed,
    setSuccess: setProcessSuccess,
    setDelayed: setProcessDelayed,
    close: closeProcessModal,
  } = rechargeProcessModal;

  const beginCheckoutProcessUi = useCallback(() => {
    setOpenWalletPaymentMethod(false);
    startProcessModal();
  }, [startProcessModal]);

  const resolveFailedMessage = useCallback(
    (message?: string) => message || DEFAULT_RECHARGE_FAILED_MESSAGE,
    [],
  );

  useEffect(() => {
    if (
      !processModalOpen ||
      processModalStatus !== "processing" ||
      !activeRechargeId
    )
      return;

    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;
    let stopped = false;

    const stop = () => {
      stopped = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };

    const poll = () => {
      if (stopped) return;

      RechargeFulfill(activeRechargeId)
        .then((res) => {
          const data = res?.data;

          if (data?.statusCode === AppStatusCode.api_success) {
            const STATUS = data?.data?.status?.toLowerCase();
            if (STATUS === "success") {
              setProcessSuccess();
              stop();
              if (openWalletPaymentMethod) setOpenWalletPaymentMethod(false);
              navigate(RouteConstant.MY_ACCOUNT);
            } else if (STATUS === "pending") {
              // keep polling
            } else {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
              stop();
            }
          } else {
            setProcessFailedMessage(resolveFailedMessage(data?.message));
            setProcessFailed();
            stop();
          }
        })
        .catch((error) => {
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          setProcessFailed();
          stop();
        });
    };

    poll();
    intervalId = setInterval(poll, RECHARGE_POLL_SECONDS * 1000);
    timeoutId = setTimeout(() => {
      stop();
      setProcessDelayed();
    }, RECHARGE_POLL_TIMEOUT_SECONDS * 1000);

    return () => stop();
  }, [
    processModalOpen,
    processModalStatus,
    activeRechargeId,
    navigate,
    openWalletPaymentMethod,
    RECHARGE_POLL_TIMEOUT_SECONDS,
    setProcessFailed,
    setProcessSuccess,
    setProcessDelayed,
  ]);

  const rechargeDetails = useMemo(() => {
    return [
      {
        label: "Phone Number",
        value:
          processingSnapshot.phoneNumber ||
          rechargeData?.phoneNumber ||
          userInfo?.phone ||
          "-",
      },
      {
        label: "Amount",
        value:
          typeof processingSnapshot.amount === "number"
            ? `UGX ${formatPriceWithDelimiter(processingSnapshot.amount)}`
            : "-",
      },
      {
        label: "Plan Details",
        value: processingSnapshot.packageBenefit || "-",
        fullWidth: true,
      },
    ];
  }, [processingSnapshot, rechargeData?.phoneNumber, userInfo?.phone]);

  const [walletPaymentState, setWalletPaymentState] =
    useState<WalletPaymentModel>(new WalletPaymentModel());

  const [couponState, setCouponState] = useState<ApplyCouponModel>(
    new ApplyCouponModel(),
  );
  const [couponErrors, setCouponErrors] = useState<ApplyCouponErrorModel>(
    new ApplyCouponErrorModel(),
  );

  const handleToggleBuildBundleRechargeOpen = () => {
    setCouponState(new ApplyCouponModel());
    setCouponErrors(new ApplyCouponErrorModel());
    setBuildBundleRechargeOpen(!buildBundleRechargeOpen);
  };

  const handleTogglePaymentOption = () => {
    setCouponState(new ApplyCouponModel());
    setCouponErrors(new ApplyCouponErrorModel());
    if (openPaymentOption) {
      Dispatch(DispatchRechargeData({ data: null }));
    }
    setOpenPaymentOption(!openPaymentOption);
  };

  const handleToggleWalletPaymentMethod = () =>
    setOpenWalletPaymentMethod(!openWalletPaymentMethod);

  const handleApplyCoupon = (DATA: ApplyCouponModel) => {
    if (
      !DATA?.coupon ||
      (!rechargeData?.data?.retailPrice &&
        !bookingData?.buildBundleDetails?.price?.totalPrice)
    ) {
      return Dispatch(
        setAlert({ message: `Mandatory data missing!`, type: "error" }),
      );
    }
    const PAYLOAD_DATA: CouponCheckApplicabilityProps["DATA"] = {
      couponCode: DATA.coupon,
      amount:
        rechargeData.data?.retailPrice ||
        bookingData?.buildBundleDetails?.price?.totalPrice ||
        0,
      applyOn: CouponApplyOnEnum.recharge,
    };
    Dispatch(toggleLoading(true));
    CouponCheckApplicability({ DATA: PAYLOAD_DATA })
      .then((res) => {
        const data = res?.data;
        if (
          data?.statusCode === AppStatusCode.api_success &&
          data?.data?.isApplicable
        ) {
          setCouponState((prev) => ({
            ...prev,
            ...(data?.data || {}),
          }));
        } else {
          Dispatch(
            setAlert({
              type: "error",
              message: data?.data?.reason || "Something went wrong!",
            }),
          );
          setCouponState(() => ({
            coupon: "",
            isApplied: false,
          }));
        }
      })
      .catch((error) => {
        Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
      })
      .finally(() => Dispatch(toggleLoading(false)));
  };

  const handleRemoveCoupon = () => {
    setCouponState(new ApplyCouponModel());
  };

  let handleRechargeRequest = (DATA: PaymentMethodDataProps) => {
    if (
      DATA?.paymentBy &&
      DATA?.paymentMethod &&
      rechargeData?.data?._id &&
      rechargeData?.phoneNumber
    ) {
      let PAYLOAD_DATA: BundleRechargeRequestProps["DATA"] = {
        bundleId: rechargeData.data._id,
        phoneNumber: rechargeData.phoneNumber,
        paymentMethod: DATA.paymentMethod,
        paymentBy: DATA.paymentBy,
        momoPhoneNumber: DATA.momoPhoneNumber || undefined,
        airtelMOMOPhone: DATA.airtelMOMOPhone || undefined,
        couponCode:
          couponState?.isApplicable && couponState?.coupon
            ? couponState.coupon
            : undefined,
      };

      PAYLOAD_DATA = filterNonNullValues(PAYLOAD_DATA);

      setProcessingSnapshot({
        phoneNumber: rechargeData?.phoneNumber,
        amount: Number(
          (couponState?.isApplicable && couponState?.finalAmount) ||
            rechargeData?.data?.retailPrice ||
            0,
        ),
        paymentBy: DATA.paymentBy,
        paymentMethod: DATA.paymentMethod,
        packageName: rechargeData?.data?.packageName || "Recharge Bundle",
        packageType: Array.isArray(rechargeData?.data?.packageType)
          ? rechargeData?.data?.packageType?.join(", ")
          : "Bundle Recharge",
        packageBenefit: [
          rechargeData?.data?.dataVolume?.quantity &&
          rechargeData?.data?.dataVolume?.dataUnit
            ? `Data: ${formatPriceWithDelimiter(
                Number(rechargeData.data.dataVolume.quantity),
              )} ${rechargeData.data.dataVolume.dataUnit}`
            : null,
          Number(rechargeData?.data?.onNetMinutes || 0) > 0
            ? `On-net voice: ${formatCount(rechargeData?.data?.onNetMinutes)} mins`
            : null,
          Number(rechargeData?.data?.offNetMinutes || 0) > 0
            ? `All-net voice: ${formatCount(rechargeData?.data?.offNetMinutes)} mins`
            : null,
          Number(rechargeData?.data?.allNetSMS || 0) > 0
            ? `SMS: ${formatCount(rechargeData?.data?.allNetSMS)}`
            : null,
          rechargeData?.data?.validity
            ? `Validity: ${rechargeData?.data?.validity} day(s)`
            : null,
        ]
          .filter(Boolean)
          .join(" | "),
        couponCode:
          couponState?.isApplicable && couponState?.coupon
            ? couponState.coupon
            : undefined,
      });

      if (DATA.paymentBy !== "wallet") {
        setProcessFailedMessage("");
        beginCheckoutProcessUi();
        setActiveRechargeId("");
      } else {
        Dispatch(toggleLoading(true));
      }
      RechargeBundle({ DATA: PAYLOAD_DATA })
        .then((res) => {
          const data = res?.data;
          const rechargeId = data?.data?.rechargeId;

          if (data?.statusCode === AppStatusCode.api_success && rechargeId) {
            if (DATA.paymentBy === "wallet") {
              const walletPaymentId = data?.data?.walletRequestId;
              setWalletPaymentState((prev) => ({
                ...prev,
                walletPaymentId: walletPaymentId,
                rechargeId: rechargeId,
              }));
              handleToggleWalletPaymentMethod();
              Dispatch(toggleLoading(false));
            } else {
              setActiveRechargeId(rechargeId);
            }
          } else {
            if (DATA.paymentBy !== "wallet") {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
            } else {
              Dispatch(
                setAlert({
                  type: data?.level,
                  message: data?.message || "Something went wrong!",
                }),
              );
              Dispatch(toggleLoading(false));
            }
          }
        })
        .catch((error) => {
          if (axios.isCancel(error)) return; // Ignore cancel errors
          Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          setProcessFailed();
          Dispatch(toggleLoading(false));
        });
    }
  };

  let handleBuildBundleRechargeRequest = (DATA: PaymentMethodDataProps) => {
    if (DATA?.paymentBy && DATA?.paymentMethod && rechargeData?.phoneNumber) {
      let PAYLOAD_DATA: BuildBundleRechargeRequestProps["DATA"] = {
        // bundleId: rechargeData.bundleId,
        onNetVoice: bookingData?.buildBundleDetails?.unit?.onNetVoice || 0,
        offNetVoice: bookingData?.buildBundleDetails?.unit?.offNetVoice || 0,
        dataMb: bookingData?.buildBundleDetails?.unit?.dataMb || 0,
        smsOffNet: bookingData?.buildBundleDetails?.unit?.smsOffNet || 0,
        intervalId: bookingData?.buildBundleIntervalId || undefined,
        phoneNumber: rechargeData.phoneNumber,
        paymentMethod: DATA.paymentMethod,
        paymentBy: DATA.paymentBy,
        momoPhoneNumber: DATA.momoPhoneNumber || undefined,
        airtelMOMOPhone: DATA.airtelMOMOPhone || undefined,
        couponCode:
          couponState?.isApplicable && couponState?.coupon
            ? couponState.coupon
            : undefined,
      };

      setProcessingSnapshot({
        phoneNumber: rechargeData?.phoneNumber,
        amount: Number(
          (couponState?.isApplicable && couponState?.finalAmount) ||
            bookingData?.buildBundleDetails?.price?.totalPrice ||
            0,
        ),
        paymentBy: DATA.paymentBy,
        paymentMethod: DATA.paymentMethod,
        packageName: "Build Bundle",
        packageType: "Build Bundle",
        packageBenefit: [
          Number(bookingData?.buildBundleDetails?.unit?.dataMb || 0) > 0
            ? `Data: ${formatDataUsage(
                bookingData?.buildBundleDetails?.unit?.dataMb || 0,
              )}`
            : null,
          Number(bookingData?.buildBundleDetails?.unit?.onNetVoice || 0) > 0
            ? `On-net voice: ${formatCount(
                bookingData?.buildBundleDetails?.unit?.onNetVoice,
              )} mins`
            : null,
          Number(bookingData?.buildBundleDetails?.unit?.offNetVoice || 0) > 0
            ? `All-net voice: ${formatCount(
                bookingData?.buildBundleDetails?.unit?.offNetVoice,
              )} mins`
            : null,
          Number(bookingData?.buildBundleDetails?.unit?.smsOffNet || 0) > 0
            ? `SMS: ${formatCount(bookingData?.buildBundleDetails?.unit?.smsOffNet)}`
            : null,
        ]
          .filter(Boolean)
          .join(" | "),
        couponCode:
          couponState?.isApplicable && couponState?.coupon
            ? couponState.coupon
            : undefined,
      });

      if (DATA.paymentBy !== "wallet") {
        setProcessFailedMessage("");
        beginCheckoutProcessUi();
        setActiveRechargeId("");
      } else {
        Dispatch(toggleLoading(true));
      }
      RechargeBuildBundle({ DATA: PAYLOAD_DATA })
        .then((res) => {
          const data = res?.data;
          const rechargeId = data?.data?.rechargeId;

          if (data?.statusCode === AppStatusCode.api_success && rechargeId) {
            if (DATA.paymentBy === "wallet") {
              const walletPaymentId = data?.data?.walletRequestId;
              setWalletPaymentState((prev) => ({
                ...prev,
                walletPaymentId: walletPaymentId,
                rechargeId: rechargeId,
              }));
              handleToggleWalletPaymentMethod();
              Dispatch(toggleLoading(false));
            } else {
              setActiveRechargeId(rechargeId);
            }
          } else {
            if (DATA.paymentBy !== "wallet") {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
            } else {
              Dispatch(
                setAlert({
                  type: data?.level,
                  message: data?.message || "Something went wrong!",
                }),
              );
              Dispatch(toggleLoading(false));
            }
          }
        })
        .catch((error) => {
          Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          setProcessFailed();
          Dispatch(toggleLoading(false));
        });
    }
  };

  const handleCloseAllPlansCheckoutModals = useCallback(() => {
    closeProcessModal();
    setActiveRechargeId("");
    setOpenWalletPaymentMethod(false);
    setWalletPaymentState(new WalletPaymentModel());
    Dispatch(toggleLoading(false));
    if (openPaymentOption) {
      Dispatch(DispatchRechargeData({ data: null }));
    }
    setCouponState(new ApplyCouponModel());
    setCouponErrors(new ApplyCouponErrorModel());
    setOpenPaymentOption(false);
    setBuildBundleRechargeOpen(false);
  }, [closeProcessModal, Dispatch, openPaymentOption]);

  const plansEmbeddedPaymentProcess = useMemo(() => {
    if (!processModalOpen) return null;
    const paidWithWallet = processingSnapshot.paymentBy === "wallet";
    return {
      status: processModalStatus,
      processingTitle: "Recharge is being processed",
      successTitle: "Recharge successful",
      failedTitle: "Recharge failed",
      delayedTitle: "Recharge confirmation pending",
      processingMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and complete your recharge."
        : "Please do not press back, close this window, or refresh the page while we confirm your recharge.",
      successMessage: "Your recharge has been completed successfully.",
      failedMessage: processFailedMessage || DEFAULT_RECHARGE_FAILED_MESSAGE,
      delayedMessage:
        "This recharge is still being processed. Please wait up to 30 minutes. Your recharge will be completed automatically once the payment gateway confirms it. If it is still not updated after 30 minutes, please contact support.",
      processingWarningMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and complete your recharge."
        : "Please do not press back, close this window, or refresh the page while we confirm your recharge.",
      details: rechargeDetails,
      onPrimaryAction:
        processModalStatus === "failed" || processModalStatus === "delayed"
          ? () => {
              handleCloseAllPlansCheckoutModals();
              navigate(RouteConstant.CONTACT_US);
            }
          : undefined,
      primaryActionLabel: "Contact Support",
      onClose: handleCloseAllPlansCheckoutModals,
    };
  }, [
    processModalOpen,
    processModalStatus,
    processFailedMessage,
    processingSnapshot.paymentBy,
    processCountdownLabel,
    rechargeDetails,
    DEFAULT_RECHARGE_FAILED_MESSAGE,
    handleCloseAllPlansCheckoutModals,
    navigate,
  ]);

  const handleRechargeRequestUpdate = (ID: string) => {
    Dispatch(toggleLoading(false));
    setActiveRechargeId(ID);
    beginCheckoutProcessUi();
  };

  useEffect(() => {
    if (userInfo.phone) {
      Dispatch(DispatchRechargeData({ phoneNumber: userInfo.phone }));
    }
  }, [userInfo?.phone, Dispatch]);

  const planCheckoutBill: CheckoutBillInterface[] = [
    {
      label: "",
      component: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {rechargeData?.data?.retailPrice ? (
            <Box>
              <Typography
                fontSize={24}
                fontWeight={800}
                sx={{
                  textDecoration: couponState?.isApplicable
                    ? "line-through"
                    : "none",
                  color: couponState?.isApplicable
                    ? palette.error.main
                    : "inherit",
                }}
              >
                UGX{" "}
                {formatPriceWithDelimiter(rechargeData?.data?.retailPrice || 0)}
              </Typography>
              {couponState?.isApplicable &&
              couponState?.potentialDiscount &&
              couponState?.finalAmount ? (
                <>
                  <Typography fontSize={12} fontWeight={500}>
                    Discount Applied{" "}
                    {couponState?.couponInfo?.title
                      ? `(${couponState?.couponInfo?.title})`
                      : null}
                    : -UGX{" "}
                    {formatPriceWithDelimiter(couponState?.potentialDiscount)}
                  </Typography>
                  <Typography fontSize={20} fontWeight={600}>
                    Total Payable: UGX{" "}
                    {formatPriceWithDelimiter(couponState?.finalAmount)}
                  </Typography>
                </>
              ) : null}
            </Box>
          ) : null}

          {Array.isArray(rechargeData?.data?.packageType) ? (
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 1,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {rechargeData?.data?.packageType?.map((type, index) => {
                const { bg, color } =
                  ChipColorStyles[index % ChipColorStyles.length];
                return (
                  <Chip
                    key={type}
                    label={type.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: bg,
                      color: color,
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </Box>
          ) : null}

          {rechargeData?.data?._id ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography fontSize={14} fontWeight={500}>
                Plan Details
              </Typography>
              <Box
                sx={{
                  bgcolor: palette.grey[100],
                  p: 1,
                  borderRadius: 2,
                }}
              >
                <PlanCardFour data={rechargeData?.data} showOnlyPlanDetails />
              </Box>
            </Box>
          ) : null}
        </Box>
      ),
      enabled: Boolean(rechargeData?.data),
    },
  ];

  const buildBundleCheckoutBill: CheckoutBillInterface[] = [
    {
      label: "",
      component: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {bookingData?.buildBundleDetails?.unit &&
          bookingData?.buildBundleDetails?.price ? (
            <Box>
              <Typography
                fontSize={24}
                fontWeight={800}
                sx={{
                  textDecoration: couponState?.isApplicable
                    ? "line-through"
                    : "none",
                  color: couponState?.isApplicable
                    ? palette.error.main
                    : "inherit",
                }}
              >
                UGX{" "}
                {formatPriceWithDelimiter(
                  bookingData?.buildBundleDetails?.price?.totalPrice || 0,
                )}
              </Typography>
              {couponState?.isApplicable &&
              couponState?.potentialDiscount &&
              couponState?.finalAmount ? (
                <>
                  <Typography fontSize={12} fontWeight={500}>
                    Discount Applied{" "}
                    {couponState?.couponInfo?.title
                      ? `(${couponState?.couponInfo?.title})`
                      : null}
                    : -UGX{" "}
                    {formatPriceWithDelimiter(couponState?.potentialDiscount)}
                  </Typography>
                  <Typography fontSize={20} fontWeight={600}>
                    Total Payable: UGX{" "}
                    {formatPriceWithDelimiter(couponState?.finalAmount)}
                  </Typography>
                </>
              ) : null}
            </Box>
          ) : null}

          {bookingData?.buildBundleDetails ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography fontSize={14} fontWeight={500}>
                Build Bundle Details
              </Typography>

              <BuildBundleCard data={bookingData?.buildBundleDetails} />
            </Box>
          ) : null}
        </Box>
      ),
      enabled: Boolean(bookingData?.buildBundleDetails),
    },
  ];

  return (
    <>
      <section>
        <MainContainer>
          <Box py={5}>
            <Box maxWidth={400}>
              <SummaryNumber
                phoneNumber={rechargeData?.phoneNumber}
                title="Recharging for"
                showChangeButton
              />
            </Box>
            <Grid container spacing={3}>
              {isMediumDevice ? (
                <Grid item xs={12} md={4} lg={3}>
                  <FilterPlans filters={filters} setFilters={setFilters} />
                </Grid>
              ) : (
                <></>
              )}
              <Grid item xs={12} md={8} lg={9}>
                <BuildBundleRecharge
                  handleToggleBuildBundleRechargeOpen={
                    handleToggleBuildBundleRechargeOpen
                  }
                />
                <AllPlans
                  filters={filters}
                  setFilters={setFilters}
                  handleSelectPlan={handleTogglePaymentOption}
                />
              </Grid>
            </Grid>
          </Box>
        </MainContainer>
      </section>

      {/* PAYMENT METHOD */}
      <Dialog
        open={openPaymentOption || buildBundleRechargeOpen}
        maxWidth="md"
        fullWidth
      >
        <PaymentCheckout
          checkoutTitle={
            rechargeData?.phoneNumber
              ? `Recharge ${rechargeData?.phoneNumber}`
              : ""
          }
          checkoutBill={
            openPaymentOption
              ? planCheckoutBill
              : buildBundleRechargeOpen
                ? buildBundleCheckoutBill
                : []
          }
          handleMakePaymentRequest={
            openPaymentOption
              ? handleRechargeRequest
              : buildBundleRechargeOpen
                ? handleBuildBundleRechargeRequest
                : () => {}
          }
          togglePaymentCheckout={
            openPaymentOption
              ? handleTogglePaymentOption
              : buildBundleRechargeOpen
                ? handleToggleBuildBundleRechargeOpen
                : () => {}
          }
          paymentOptions={paymentOptionsList}
          isCouponFormEnabled
          couponState={couponState}
          setCouponState={setCouponState}
          couponErrors={couponErrors}
          setCouponErrors={setCouponErrors}
          handleApplyCoupon={handleApplyCoupon}
          handleRemoveCoupon={handleRemoveCoupon}
          embeddedPaymentProcess={plansEmbeddedPaymentProcess}
        />
      </Dialog>

      {/* WALLET PAYMENT METHOD */}
      <Dialog open={openWalletPaymentMethod} fullWidth maxWidth="sm">
        <WalletPaymentMethod
          handleClose={handleToggleWalletPaymentMethod}
          walletPaymentState={walletPaymentState}
          handleContinue={handleRechargeRequestUpdate}
        />
      </Dialog>
    </>
  );
};

export default RechargePlansLayout;
